/**
 * PieChartCard — Recharts donut wrapper styled for the dashboard. Slices are
 * sized by `value` (e.g. invoice count); the optional `amountCents` rides
 * along for the tooltip only. Mirrors BarChartCard's conventions (per-slice
 * "R,G,B" colors via formatRgb, loading skeleton, "No data" fallback).
 *
 * The donut center can show a big total via `centerValue` + `centerLabel`.
 * Hover tooltip shows label · count · $ — but on touch (no hover) the caller
 * should pair this with a StatusList legend so the data is always readable.
 */

import * as React from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { formatRgb, formatCurrencyCents } from "./types";

export interface PieChartCardData {
  label: string;
  /** Slice size — the measure the pie is drawn by (count). */
  value: number;
  /** Optional $ total for this slice, shown in the tooltip. */
  amountCents?: number;
  /**
   * Optional remaining balance, shown in the tooltip under the total
   * (e.g. the Partially Paid slice shows both figures).
   */
  balanceCents?: number | null;
  /** Per-slice color ("R,G,B" triplet). Falls back to gray. */
  color?: string;
}

export interface PieChartCardProps {
  data: PieChartCardData[];
  loading?: boolean;
  /**
   * Donut height — a pixel number, or any CSS value (e.g. "100%") to fill the
   * parent. When filling, give the parent a concrete height (e.g. aspect-square)
   * so the chart doesn't collapse to zero inside a flex/auto-height container.
   * Defaults to 180.
   */
  height?: number | string;
  /** Big number in the donut center (e.g. total count). */
  centerValue?: number | string;
  /** Small caption under the center value. */
  centerLabel?: string;
  /** Second caption under the center label (e.g. the total $ value). */
  centerSubLabel?: string;
}

export function PieChartCard({
  data,
  loading,
  height = 180,
  centerValue,
  centerLabel,
  centerSubLabel,
}: PieChartCardProps) {
  if (loading) {
    const skeletonSize = typeof height === "number" ? height : "100%";
    return (
      <div
        className="bg-gray-100 rounded-full animate-pulse mx-auto"
        style={{ height: skeletonSize, width: skeletonSize }}
        aria-label="loading"
      />
    );
  }
  if (data.length === 0) {
    return <div className="text-xs text-gray-400">No data</div>;
  }

  return (
    <div className="relative w-full" style={{ height }}>
      {/* Center total sits BEHIND the chart layer (shows through the donut
          hole) so the hover tooltip is never obscured by it. */}
      {centerValue !== undefined && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
          style={{ zIndex: 0 }}
        >
          <span className="text-xl font-semibold text-gray-800 tabular-nums">
            {centerValue}
          </span>
          {centerLabel && <span className="text-[10px] text-gray-500">{centerLabel}</span>}
          {centerSubLabel && (
            <span className="text-[11px] font-medium text-gray-700 tabular-nums">
              {centerSubLabel}
            </span>
          )}
        </div>
      )}
      <div className="relative h-full" style={{ zIndex: 10 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="label"
              innerRadius="62%"
              outerRadius="88%"
              paddingAngle={data.length > 1 ? 2 : 0}
              stroke="none"
              isAnimationActive={false}
            >
              {data.map((entry, i) => (
                <Cell key={i} fill={formatRgb(entry.color)} />
              ))}
            </Pie>
            <Tooltip content={<PieTooltip />} wrapperStyle={{ zIndex: 20 }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

interface PieTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: PieChartCardData }>;
}

function PieTooltip({ active, payload }: PieTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const slice = payload[0].payload;
  return (
    <div
      style={{
        fontSize: 12,
        border: "1px solid #e5e7eb",
        borderRadius: 4,
        padding: "4px 8px",
        background: "#fff",
      }}
    >
      <div style={{ fontWeight: 600 }}>{slice.label}</div>
      <div>
        {slice.value.toLocaleString("en-US")} invoice{slice.value === 1 ? "" : "s"}
      </div>
      {slice.amountCents !== undefined && <div>{formatCurrencyCents(slice.amountCents)}</div>}
      {slice.balanceCents != null && (
        <div style={{ color: "#6b7280" }}>
          {formatCurrencyCents(slice.balanceCents)} remaining
        </div>
      )}
    </div>
  );
}
